import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { TooltipLabel } from '../ui/TooltipLabel';
import { costManagementTooltips } from '../../utils/strategicFrameworkTooltips';
import { useCurrency } from '../../contexts/CurrencyContext';

/**
 * Per-year growth rate table for costs
 * Allows different growth rate for each year (2025-2029)
 */
const PerYearGrowthTable = ({ form, setForm }) => {
  const { formatCurrency } = useCurrency();
  
  const calculateProjectedCost = (year) => {
    const baseAmount = parseFloat(form.amount) || 0;
    let projected = baseAmount;
    
    for (let y = 2026; y <= year; y++) {
      const rate = form.growth_rates_per_year?.[y] || 0;
      projected = projected * (1 + rate / 100);
    }
    
    return projected;
  };

  const handleToggle = (checked) => {
    setForm({
      ...form,
      use_per_year_rates: checked,
      growth_rates_per_year: checked ? {
        2025: parseFloat(form.annual_growth_rate) || 0,
        2026: parseFloat(form.annual_growth_rate) || 0,
        2027: parseFloat(form.annual_growth_rate) || 0,
        2028: parseFloat(form.annual_growth_rate) || 0,
        2029: parseFloat(form.annual_growth_rate) || 0
      } : {}
    });
  };

  const updateYearRate = (year, value) => {
    setForm({
      ...form,
      growth_rates_per_year: {
        ...form.growth_rates_per_year,
        [year]: parseFloat(value) || 0
      }
    });
  };

  return (
    <div className="border rounded-lg p-4 bg-blue-50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-blue-800">Annual Cost Growth</h4>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.use_per_year_rates}
            onChange={(e) => handleToggle(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-blue-700">Different rate each year</span>
        </label>
      </div>

      {!form.use_per_year_rates ? (
        <div>
          <TooltipLabel 
            htmlFor="annual-growth" 
            tooltip={costManagementTooltips.growthRates?.perYear || 'Annual cost increase rate'}
          >
            Annual Growth Rate (%)
          </TooltipLabel>
          <Input
            id="annual-growth"
            type="number"
            value={form.annual_growth_rate}
            onChange={(e) => setForm({...form, annual_growth_rate: e.target.value})}
            placeholder="e.g., 5"
            className="mt-1"
          />
          <p className="text-xs text-blue-600 mt-1">
            Same growth rate applied to all years
          </p>
        </div>
      ) : (
        <div>
          <p className="text-xs font-medium text-blue-700 mb-2">Set growth rate for each year</p>
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-3 py-2 text-left text-blue-800">Year</th>
                  <th className="px-3 py-2 text-left text-blue-800">Growth Rate (%)</th>
                  <th className="px-3 py-2 text-right text-blue-800">Projected Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[2025, 2026, 2027, 2028, 2029].map((year, index) => {
                  const projected = index === 0 ? parseFloat(form.amount) || 0 : calculateProjectedCost(year);
                  
                  return (
                    <tr key={year} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{year}</td>
                      <td className="px-3 py-2">
                        {index === 0 ? (
                          <span className="text-gray-500">Base Year</span>
                        ) : (
                          <Input
                            type="number"
                            value={form.growth_rates_per_year?.[year] || 0}
                            onChange={(e) => updateYearRate(year, e.target.value)}
                            className="w-20 text-right"
                            placeholder="0"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 font-medium">
                        {formatCurrency(projected)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Growth compounds year-over-year. Example: $1000 → $1050 (+5%) → $1134 (+8%) → $1168 (+3%)
          </p>
        </div>
      )}
    </div>
  );
};

export default PerYearGrowthTable;
